import React, { useState } from 'react';
import { toast as message } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { supabaseClient, supabaseCoreClient } from '../../../utils/supabase';
import type { ActionDialogProps } from '../types';
import EnhancedDialog from '../../EnhancedDialog';

export const AchChargeDialog: React.FC<ActionDialogProps> = ({
  isOpen,
  onClose,
  prefill,
  onSuccess,
}) => {
  const [amountDollars, setAmountDollars] = useState<string>('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [autoChargeOnApproval, setAutoChargeOnApproval] = useState(true);

  const { data: isApprover } = useQuery({
    queryKey: ['is_ach_approver'],
    queryFn: async () => {
      const { data } = await (supabaseCoreClient as any)
        .rpc('is_user_in_group', { p_group_name: 'ACH Charge Approvers' });
      return !!data;
    },
    staleTime: 60_000,
  });

  const amount = parseFloat(amountDollars);
  const validAmount = !isNaN(amount) && amount > 0;

  const handleSubmit = async () => {
    if (!validAmount) return;
    setSubmitting(true);

    try {
      const { data, error } = await supabaseClient.functions.invoke('ach-charge-run', {
        body: {
          ach_mandate_id: Number(prefill.ach_mandate_id),
          amount_cents: Math.round(amount * 100),
          description: description || undefined,
          ...(!isApprover ? { auto_charge_on_approval: autoChargeOnApproval } : {}),
        },
      });

      if (data?.error) throw new Error(data.error);
      if (error) throw error;

      message.success(`Charge created — status: ${data.status}`);
      resetForm();
      onClose();
      onSuccess?.();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to create charge';
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setAmountDollars('');
    setDescription('');
  };

  const handleClose = () => {
    if (!submitting) {
      resetForm();
      onClose();
    }
  };

  return (
    <EnhancedDialog
      isOpen={isOpen}
      onClose={handleClose}
      title="Create ACH Charge"
      style={{ width: 420 }}
      canOutsideClickClose={!submitting}
    >
      <div className="sky-dialog-body">
        <div style={{ marginBottom: 12 }}>
          <span style={{ display: 'inline-block', padding: '2px 8px', fontSize: 11.5, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-2)' }}>
            Mandate #{String(prefill.ach_mandate_id)}
          </span>
        </div>

        <div className="sky-form-group">
          <label className="sky-form-label">Amount (USD) <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(required)</span></label>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', fontSize: 13 }}>$</span>
            <input
              className="sky-field-input"
              type="number"
              value={amountDollars}
              onChange={(e) => setAmountDollars(e.target.value)}
              min="0.01"
              step="0.01"
              placeholder="0.00"
              style={{ paddingLeft: 24 }}
            />
          </div>
        </div>

        <div className="sky-form-group">
          <label className="sky-form-label">Description</label>
          <input
            className="sky-field-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What is this charge for?"
          />
        </div>

        {isApprover === false && (
          <>
            <div className="sky-callout" style={{ marginBottom: 12 }}>
              This charge requires approval before processing. An approver will review it.
            </div>
            <div className="sky-form-group">
              <label className="sky-form-label">Auto-charge on approval</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12.5 }}>
                <input
                  type="checkbox"
                  checked={autoChargeOnApproval}
                  onChange={() => setAutoChargeOnApproval((v) => !v)}
                />
                {autoChargeOnApproval ? 'Charge executes automatically when approved' : 'Charge must be executed manually after approval'}
              </label>
            </div>
          </>
        )}

        <div className="sky-callout sky-callout-warning">
          ACH charges typically take 4-5 business days to settle.
        </div>
      </div>

      <div className="sky-dialog-footer">
        <button className="sky-btn sky-btn-ghost" onClick={handleClose} disabled={submitting}>Cancel</button>
        <button
          className="sky-btn sky-btn-primary"
          onClick={handleSubmit}
          disabled={submitting || !validAmount}
        >
          {submitting ? <span className="sky-spinner" /> : null}
          Create Charge
        </button>
      </div>
    </EnhancedDialog>
  );
};
